(() => {
  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'TRY',
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ₺`;
    }
  };

  const ISTANBUL_TIME_ZONE = 'Europe/Istanbul';

  const datePartsInIstanbul = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: ISTANBUL_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    return Object.fromEntries(parts.filter(({ type }) => type !== 'literal').map(({ type, value }) => [type, value]));
  };

  const isoFromDate = (date) => date.toISOString().slice(0, 10);
  const dateFromIso = (iso) => new Date(`${iso}T12:00:00Z`);
  const weekdayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const defaultSchedule = {
    timezone: ISTANBUL_TIME_ZONE,
    weekly: {
      monday: { enabled: true, opensAt: '00:00', cutoffAt: '14:00' },
      tuesday: { enabled: true, opensAt: '00:00', cutoffAt: '14:00' },
      wednesday: { enabled: true, opensAt: '00:00', cutoffAt: '14:00' },
      thursday: { enabled: true, opensAt: '00:00', cutoffAt: '14:00' },
      friday: { enabled: true, opensAt: '00:00', cutoffAt: '14:00' },
      saturday: { enabled: false, opensAt: '00:00', cutoffAt: '14:00' },
      sunday: { enabled: false, opensAt: '00:00', cutoffAt: '14:00' },
    },
    closedDates: [],
  };

  const timeToMinutes = (value) => {
    const [hours, minutes] = String(value || '').split(':').map(Number);
    return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : 0;
  };

  const normalizeSchedule = (candidate) => {
    if (!candidate || typeof candidate !== 'object' || !candidate.weekly) return defaultSchedule;
    const weekly = {};
    Object.keys(defaultSchedule.weekly).forEach((key) => {
      const defaults = defaultSchedule.weekly[key];
      const source = candidate.weekly[key] || {};
      weekly[key] = {
        enabled: source.enabled === true,
        opensAt: /^([01]\d|2[0-3]):[0-5]\d$/.test(source.opensAt || '') ? source.opensAt : defaults.opensAt,
        cutoffAt: /^([01]\d|2[0-3]):[0-5]\d$/.test(source.cutoffAt || '') ? source.cutoffAt : defaults.cutoffAt,
      };
    });
    return {
      timezone: ISTANBUL_TIME_ZONE,
      weekly,
      closedDates: Array.isArray(candidate.closedDates) ? candidate.closedDates.filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)) : [],
    };
  };

  const scheduleDay = (schedule, iso) => {
    const weekday = weekdayKeys[dateFromIso(iso).getUTCDay()];
    const day = schedule.weekly[weekday];
    return {
      ...day,
      enabled: Boolean(day?.enabled) && !schedule.closedDates.includes(iso),
    };
  };

  const isAvailableDate = (schedule, iso) => Boolean(iso && scheduleDay(schedule, iso).enabled);

  const nextAvailableDate = (schedule, iso) => {
    const date = dateFromIso(iso);
    for (let index = 0; index < 370; index += 1) {
      date.setUTCDate(date.getUTCDate() + 1);
      const candidate = isoFromDate(date);
      if (isAvailableDate(schedule, candidate)) return candidate;
    }
    return '';
  };

  const deliverySchedule = (schedule) => {
    const now = datePartsInIstanbul();
    const today = `${now.year}-${now.month}-${now.day}`;
    const todaySchedule = scheduleDay(schedule, today);
    const currentTime = Number(now.hour) * 60 + Number(now.minute);
    const canProcessToday = todaySchedule.enabled
      && currentTime >= timeToMinutes(todaySchedule.opensAt)
      && currentTime < timeToMinutes(todaySchedule.cutoffAt);
    return {
      today,
      todaySchedule,
      canProcessToday,
      firstDate: canProcessToday ? today : nextAvailableDate(schedule, today),
    };
  };

  class RituelProductExperience {
    constructor(root) {
      this.root = root;
      this.forms = [...root.querySelectorAll('[data-rdfp-purchase]')];
      this.state = { date: '', personalized: true, text: '', giftNote: '', giftSender: '', delivery: '' };
      this.schedule = defaultSchedule;
      this.busy = false;
      this.init();
      this.loadSchedule();
    }

    async loadSchedule() {
      const scheduleUrl = this.root.dataset.rdfpScheduleUrl;
      if (!scheduleUrl) return;

      try {
        const response = await fetch(scheduleUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        this.schedule = normalizeSchedule(await response.json());
        if (this.state.date && !isAvailableDate(this.schedule, this.state.date)) this.state.date = '';
        this.sync();
      } catch (_error) {
        // The embedded default keeps ordering available if the schedule service is temporarily unavailable.
      }
    }

    init() {
      this.forms.forEach((form) => {
        const date = form.querySelector('[data-rdfp-date]');
        if (form.dataset.rdfpDeliveryMode === 'courier_only') this.state.delivery = 'courier';
        this.updateDateAvailability(form);

        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.addToCart('cart', form);
        });

        form.querySelector('[data-rdfp-action="buy"]')?.addEventListener('click', () => {
          this.addToCart('buy', form);
        });

        date?.addEventListener('change', (event) => {
          this.state.date = event.target.value;
          this.sync(form);
        });

        form.querySelectorAll('[data-rdfp-delivery-method]').forEach((input) => {
          input.addEventListener('change', (event) => {
            if (event.target.type !== 'hidden' && !event.target.checked) return;
            this.state.delivery = event.target.value;
            this.sync(form);
          });
        });

        form.querySelectorAll('[data-rdfp-personalization]').forEach((radio) => {
          radio.addEventListener('change', (event) => {
            if (!event.target.checked) return;
            this.state.personalized = event.target.value === 'yes';
            if (!this.state.personalized) this.state.text = '';
            this.sync(form);
          });
        });

        form.querySelector('[data-rdfp-text]')?.addEventListener('input', (event) => {
          this.state.text = event.target.value.slice(0, 40);
          this.sync(form);
        });

        form.querySelector('[data-rdfp-gift-note]')?.addEventListener('input', (event) => {
          this.state.giftNote = event.target.value.slice(0, 300);
          this.sync(form);
        });

        form.querySelector('[data-rdfp-gift-sender]')?.addEventListener('input', (event) => {
          this.state.giftSender = event.target.value.slice(0, 60);
          this.sync(form);
        });
      });

      this.root.querySelectorAll('[data-rdfp-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          document.getElementById(button.dataset.target)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });

      const dialog = this.root.querySelector('[data-rdfp-dialog]');
      this.root.querySelectorAll('[data-rdfp-zoom]').forEach((button) => {
        button.addEventListener('click', () => dialog?.showModal());
      });
      this.root.querySelector('[data-rdfp-close]')?.addEventListener('click', () => dialog?.close());
      dialog?.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      this.sync();
    }

    sync(sourceForm) {
      this.forms.forEach((form) => {
        const date = form.querySelector('[data-rdfp-date]');
        const dateField = form.querySelector('[data-rdfp-date-field]');
        const dateLabel = form.querySelector('[data-rdfp-date-label]');
        const dateNote = form.querySelector('[data-rdfp-date-note]');
        const deliveryInputs = form.querySelectorAll('[data-rdfp-delivery-method]');
        const deliveryMode = form.dataset.rdfpDeliveryMode || 'courier_only';
        const text = form.querySelector('[data-rdfp-text]');
        const giftNote = form.querySelector('[data-rdfp-gift-note]');
        const giftSender = form.querySelector('[data-rdfp-gift-sender]');
        const panel = form.querySelector('[data-rdfp-personalization-panel]');
        const yes = form.querySelector('[data-rdfp-personalization][value="yes"]');
        const no = form.querySelector('[data-rdfp-personalization][value="no"]');
        const addonLabel = form.querySelector('[data-rdfp-addon-label]');
        const compare = form.querySelector('[data-rdfp-compare]');
        const count = form.querySelector('[data-rdfp-count]');
        const total = form.querySelector('[data-rdfp-total]');

        if (form !== sourceForm && date) date.value = this.state.date;
        if (form !== sourceForm && text) text.value = this.state.text;
        if (form !== sourceForm && giftNote) giftNote.value = this.state.giftNote;
        if (form !== sourceForm && giftSender) giftSender.value = this.state.giftSender;
        if (yes) yes.checked = this.state.personalized;
        if (no) no.checked = !this.state.personalized;
        deliveryInputs.forEach((input) => {
          if (input.type !== 'hidden') input.checked = input.value === this.state.delivery;
        });

        const needsChoice = deliveryMode === 'courier_and_cargo';
        if (dateField) dateField.hidden = needsChoice && !this.state.delivery;
        this.updateDateAvailability(form);
        if (dateLabel) dateLabel.textContent = this.state.delivery === 'cargo' ? 'Kargoya teslim tarihi' : 'Teslimat tarihi';
        if (dateNote) dateNote.textContent = this.deliveryNote(form);

        if (panel) {
          panel.hidden = !this.state.personalized;
          panel.setAttribute('aria-hidden', String(!this.state.personalized));
        }
        if (text) {
          text.disabled = !this.state.personalized;
          text.required = this.state.personalized;
        }
        if (addonLabel) addonLabel.hidden = !this.state.personalized;
        if (compare) compare.textContent = formatMoney(Number(compare.dataset.price || 0));
        if (count) count.textContent = `${this.state.text.length} / 40`;
        if (total) {
          const base = Number(form.dataset.basePrice || 0);
          const addon = this.state.personalized ? Number(form.dataset.addonPrice || 0) : 0;
          total.textContent = formatMoney(base + addon);
        }
      });
    }

    validate(form) {
      const date = form.querySelector('[data-rdfp-date]');
      const text = form.querySelector('[data-rdfp-text]');
      const status = form.querySelector('[data-rdfp-status]');
      status.textContent = '';

      if (!this.state.delivery) {
        status.textContent = 'Lütfen teslimat yönteminizi seçin.';
        return false;
      }

      if (!this.state.date || !date?.checkValidity() || !isAvailableDate(this.schedule, this.state.date)) {
        date?.reportValidity();
        status.textContent = 'Lütfen uygun bir çalışma günü seçin.';
        return false;
      }

      if (this.state.personalized && !this.state.text.trim()) {
        text?.reportValidity();
        status.textContent = 'Lütfen metal plaka için kişiselleştirme metnini girin.';
        return false;
      }

      if (this.state.personalized && !Number(form.dataset.addonVariantId)) {
        status.textContent = 'Kişiselleştirme hizmeti şu anda sepete eklenemiyor. Lütfen bizimle iletişime geçin.';
        return false;
      }
      return true;
    }

    updateDateAvailability(form) {
      const date = form.querySelector('[data-rdfp-date]');
      if (!date) return;
      date.min = deliverySchedule(this.schedule).firstDate;
    }

    deliveryNote(form) {
      const schedule = deliverySchedule(this.schedule);
      if (!this.state.delivery && form.dataset.rdfpDeliveryMode === 'courier_and_cargo') {
        return 'Önce teslimat yönteminizi seçin.';
      }
      const action = this.state.delivery === 'cargo' ? 'kargoya teslim' : 'kurye teslimatı';
      if (schedule.canProcessToday) {
        return `Bugün için ${action} seçebilirsiniz. Saat ${schedule.todaySchedule.cutoffAt}’e kadar verilen siparişler aynı gün işleme alınır.`;
      }
      if (!schedule.todaySchedule.enabled) {
        return 'Bugün siparişe kapalıdır. Uygun bir teslimat günü seçebilirsiniz.';
      }
      if (timeToMinutes(datePartsInIstanbul().hour + ':' + datePartsInIstanbul().minute) < timeToMinutes(schedule.todaySchedule.opensAt)) {
        return `Sipariş alımı bugün ${schedule.todaySchedule.opensAt}’da başlar.`;
      }
      return `Saat ${schedule.todaySchedule.cutoffAt} sonrası verilen siparişler bir sonraki uygun günde işleme alınır.`;
    }

    async addToCart(destination, form) {
      if (this.busy || !this.validate(form)) return;
      this.busy = true;
      this.setBusy(true);

      const mainProperties = {
        'Teslimat Yöntemi': this.state.delivery === 'cargo' ? 'Tüm Türkiye ücretsiz kargo' : 'Ankara içi kurye teslimatı',
        'Teslimat Tarihi': this.state.date,
        'Kişiselleştirme': this.state.personalized ? 'Evet' : 'Hayır',
      };
      if (this.state.personalized) mainProperties['Kişiselleştirme Metni'] = this.state.text.trim();
      if (this.state.giftNote.trim()) mainProperties.Notunuz = this.state.giftNote.trim();
      if (this.state.giftSender.trim()) mainProperties['Gönderici Adı'] = this.state.giftSender.trim();

      const items = [{
        id: Number(form.dataset.variantId),
        quantity: 1,
        properties: mainProperties,
      }];

      if (this.state.personalized) {
        items.push({
          id: Number(form.dataset.addonVariantId),
          quantity: 1,
          properties: {
            '_Ana Ürün': form.dataset.productTitle,
            'Kişiselleştirme Metni': this.state.text.trim(),
          },
        });
      }

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Ürün sepete eklenemedi.');
        window.location.assign(destination === 'buy' ? '/checkout' : '/cart');
      } catch (error) {
        form.querySelector('[data-rdfp-status]').textContent = error.message;
        this.busy = false;
        this.setBusy(false);
      }
    }

    setBusy(isBusy) {
      this.forms.forEach((form) => {
        form.querySelectorAll('[data-rdfp-action]').forEach((button) => {
          button.disabled = isBusy;
          if (isBusy) button.setAttribute('aria-busy', 'true');
          else button.removeAttribute('aria-busy');
        });
      });
    }
  }

  const boot = () => document.querySelectorAll('[data-rdf-product]').forEach((root) => {
    if (!root.dataset.rdfpReady) {
      root.dataset.rdfpReady = 'true';
      new RituelProductExperience(root);
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', boot);
})();
